import { toastConfig, toastErrorConfig } from 'lib/reactToastify/config';
import { lazy, Suspense, useCallback } from 'react';
import { toast } from 'react-toastify';

import { logError } from 'lib/sentry/logError';
import { Loader } from 'shared';
import lazyRetry from 'shared_DEPRECATED/utils/lazyRetry';

import {
  useAddGoalFocusAreaMutation,
  IGoal,
  useGoalQueryActions,
} from 'features/goal';

import { modal, SIZES } from 'shared/components/Modal';
import { getUuId } from 'shared/utils/uuid';

const GoalAddFocusAreaDialogContent = lazy(() =>
  lazyRetry(
    () => import('features/goal/components/FocusArea/AddDialog'),
    'GoalFocusAreaAddDialog'
  )
);

export const useAddGoalFocusArea = () => {
  const {
    cancelGoalQueries,
    getGoalQuery,
    updateGoalQueryData,
    invalidateGoalQuery,
  } = useGoalQueryActions();

  const { mutateAsync } = useAddGoalFocusAreaMutation({
    options: {
      onMutate: async ({ title, id }: { title: string; id: string }) => {
        cancelGoalQueries();

        const previousValue = getGoalQuery();

        updateGoalQueryData((oldData: IGoal) => {
          oldData.focusAreas.unshift({
            title,
            focusAreaId: id,
            challenges: [],
          });
        });

        return { previousValue };
      },
      onSuccess: (
        data: { title: string; id: string },
        variables: { title: string; id: string }
      ) => {
        updateGoalQueryData((oldData: IGoal) => {
          const focusArea = oldData.focusAreas.find(
            (area) => area.focusAreaId === variables.id
          );

          if (focusArea) {
            focusArea.focusAreaId = variables.id;
          }
        });
      },
      onError: (err: any, _: any, context: { previousValue: IGoal }) => {
        if (context) {
          updateGoalQueryData((oldData: IGoal) => {
            oldData.focusAreas = context.previousValue.focusAreas;
          });
        }

        logError(err);
        toast.error('Failed to add focus area', toastErrorConfig);
      },
      onSettled: () => {
        invalidateGoalQuery();
      },
    },
  });

  const onSubmit = useCallback(
    async ({ title }: { title: string }) => {
      try {
        const temporaryFocusAreaId = getUuId();

        await mutateAsync({ title, id: temporaryFocusAreaId });
      } catch (err) {
        toast.error(
          "There's an error with creating the focus area. Please try again later.",
          toastConfig
        );
      }
    },
    [mutateAsync]
  );

  const openAddGoalFocusAreaModal = useCallback(() => {
    modal.open({
      content: (
        <Suspense fallback={<Loader />}>
          <GoalAddFocusAreaDialogContent onSubmit={onSubmit} />
        </Suspense>
      ),
      props: {
        size: SIZES.MD,
      },
    });
  }, [onSubmit]);

  return {
    onAddFocusAreaClick: openAddGoalFocusAreaModal,
  };
};
